import React, { useCallback, useRef, useState } from "react";
import every from "lodash/every";
import {
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography
} from "@mui/material";
import { GameShell } from "../common/GameShell";
import { Row } from "../../components/Row";
import { SettingsButton } from "../../components/SettingsButton";
import { useSettings } from "../../hooks/useSettings";
import SimpleCanvas from "../gameEngines/simpleCanvas/SimpleCanvas";
import LightOnRect from "./LightOnRect";
import { solveLightsOut } from "./lightOnSolver";

const CANVAS_SIZE = 300;

function generateLightOnRectArray(
  gridSize: number,
  onAction: () => void
): LightOnRect[] {
  const size = CANVAS_SIZE / gridSize;
  const elements: LightOnRect[] = [];
  for (let y = 0; y < gridSize; y += 1) {
    for (let x = 0; x < gridSize; x += 1) {
      elements.push(
        new LightOnRect({
          name: `${x}-${y}`,
          x: x * size,
          y: y * size,
          width: size,
          height: size,
          fillColor: "#FFFFFF",
          gridx: x,
          gridy: y,
          onChange: onAction
        })
      );
    }
  }
  return elements;
}

function updateSolution(gridElements: LightOnRect[], gridSize: number): void {
  gridElements.forEach((r) => (r.solveStep = null));

  const litStates: boolean[][] = [];
  for (let y = 0; y < gridSize; y++) {
    const row: boolean[] = [];
    for (let x = 0; x < gridSize; x++) {
      const rect = gridElements.find((r) => r.gridx === x && r.gridy === y);
      row.push(rect?.lit ?? false);
    }
    litStates.push(row);
  }

  const moves = solveLightsOut(gridSize, litStates);
  if (!moves || moves.length === 0) return;

  const [x, y] = moves[0];
  const rect = gridElements.find((r) => r.gridx === x && r.gridy === y);
  if (rect) {
    rect.solveStep = 1;
  }
}

export default function LightOn() {
  const [gridSize, setGridSize] = useState(3);
  const [clicks, setClicks] = useState(0);
  const [hasWon, setHasWon] = useState(false);
  const [showingSolution, setShowingSolution] = useState(false);
  const [, forceRender] = useState(0);

  const showingSolutionRef = useRef(false);
  const gridElementsRef = useRef<LightOnRect[]>(null!);

  const [settings, updateSettings] = useSettings("lightOn-settings", {
    cheats: false
  });

  const actionTaken = useCallback(() => {
    const gridElements = gridElementsRef.current;
    const won = every(gridElements, "lit");
    if (won) {
      gridElements.forEach((el) => {
        el.active = false;
        el.solveStep = null;
      });
    }

    if (showingSolutionRef.current && !won) {
      updateSolution(
        gridElements,
        gridElements[0] ? Math.round(Math.sqrt(gridElements.length)) : 3
      );
    }

    setClicks((c) => c + 1);
    setHasWon(won);
    if (won) {
      showingSolutionRef.current = false;
      setShowingSolution(false);
    }
    forceRender((n) => n + 1);
  }, []);

  // Lazy-init grid elements
  if (gridElementsRef.current === null) {
    gridElementsRef.current = generateLightOnRectArray(gridSize, actionTaken);
  }

  const restart = useCallback(
    (size: number) => {
      setClicks(0);
      setHasWon(false);
      setShowingSolution(false);
      showingSolutionRef.current = false;
      gridElementsRef.current = generateLightOnRectArray(size, actionTaken);
      forceRender((n) => n + 1);
    },
    [actionTaken]
  );

  const handleSizeChange = useCallback(
    (event: any) => {
      const size = Number(event.target.value);
      setGridSize(size);
      restart(size);
    },
    [restart]
  );

  const handleSolve = useCallback(() => {
    const gridElements = gridElementsRef.current;
    if (showingSolutionRef.current) {
      gridElements.forEach((r) => (r.solveStep = null));
      showingSolutionRef.current = false;
      setShowingSolution(false);
      forceRender((n) => n + 1);
      return;
    }

    updateSolution(gridElements, Math.round(Math.sqrt(gridElements.length)));
    showingSolutionRef.current = true;
    setShowingSolution(true);
    forceRender((n) => n + 1);
  }, []);

  return (
    <GameShell
      title="Light On"
      subtitle="Light up every square. Each move flips a square and its neighbours."
      category="Puzzle"
      maxWidth={620}
    >
      <Stack alignItems="center" spacing={2}>
        <SimpleCanvas
          width={CANVAS_SIZE}
          height={CANVAS_SIZE}
          elements={gridElementsRef.current}
          className="game-board-canvas"
          ariaLabel="Light On puzzle"
        />
        <Row spacing={1} align="center">
          <FormControl size="small">
            <InputLabel id="size-label">Size</InputLabel>
            <Select
              labelId="size-label"
              id="size-select"
              label="Size"
              value={gridSize}
              onChange={handleSizeChange}
            >
              {[3, 4, 5, 6, 7, 8, 9].map((n) => (
                <MenuItem key={n} value={n}>
                  {n}x{n}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => restart(gridSize)}
          >
            {hasWon ? "Again?" : "Restart"}
          </Button>
          <SettingsButton>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.cheats}
                  onChange={(e) => updateSettings({ cheats: e.target.checked })}
                />
              }
              label="Cheats"
            />
          </SettingsButton>
          {settings.cheats && !hasWon ? (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleSolve}
            >
              {showingSolution ? "Hide Hint" : "Hint"}
            </Button>
          ) : null}
        </Row>
        {hasWon ? (
          <Typography> You won in {clicks} clicks</Typography>
        ) : (
          <Typography> Clicks: {clicks}</Typography>
        )}
      </Stack>
    </GameShell>
  );
}
